import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  TextField,
  MenuItem,
  Button,
  Chip,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ApiService from '../../services/api';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';

const audienceOptions = [
  { value: 'all_employers', label: 'All Employers' },
  { value: 'all_jobseekers', label: 'All Job Seekers' },
  { value: 'individual', label: 'Individual Phones' },
];

export default function SMS() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    audienceType: 'all_employers',
    message: '',
    individualPhones: [],
    sendMode: 'now', // 'now' | 'schedule'
    scheduledAt: null,
  });

  const isListOnly = useMemo(() => {
    const c = String(category || '').toLowerCase();
    return ['sent', 'draft', 'drafts', 'failed', 'fail', 'scheduled', 'auto', 'auto-send', 'autosend'].includes(c);
  }, [category]);

  const columns = [
    { field: 'id', headerName: 'ID', width: 220 },
    { field: 'audienceType', headerName: 'Audience', width: 160 },
    { field: 'status', headerName: 'Status', width: 120 },
    { field: 'totalRecipients', headerName: 'Total', width: 90 },
    { field: 'recipientsCount', headerName: 'Sent', width: 90 },
    { field: 'failedCount', headerName: 'Failed', width: 90 },
    { field: 'sentAt', headerName: 'Sent At', width: 180, valueGetter: (p) => p.row.sentAt ? new Date(p.row.sentAt).toLocaleString() : '' },
  ];

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statusMap = {
        sent: 'sent',
        draft: 'draft',
        drafts: 'draft',
        failed: 'failed',
        fail: 'failed',
        scheduled: 'scheduled',
        auto: 'scheduled',
        'auto-send': 'scheduled',
        autosend: 'scheduled',
      };
      const status = statusMap[String(category || '').toLowerCase()];
      const resp = await ApiService.getSmsMessages({ page: 1, limit: 10, ...(status ? { status } : {}) });
      const items = (resp.messages || []).map((m) => ({
        id: m._id,
        audienceType: m.audienceType,
        status: m.status,
        totalRecipients: m.totalRecipients,
        recipientsCount: m.recipientsCount,
        failedCount: m.failedCount,
        sentAt: m.sentAt,
      }));
      setList(items);
    } catch (err) {
      console.error('Failed to fetch SMS:', err);
      setError(err?.message || 'Failed to fetch SMS');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleChange = (field) => (e) => {
    const value = e?.target?.value ?? e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddPhoneChip = (e) => {
    e.preventDefault();
    const raw = String(form.tempPhone || '').trim();
    if (!raw) return;
    const next = Array.from(new Set([...(form.individualPhones || []), raw]));
    setForm((prev) => ({ ...prev, individualPhones: next, tempPhone: '' }));
  };

  const handleRemovePhoneChip = (phone) => {
    setForm((prev) => ({ ...prev, individualPhones: (prev.individualPhones || []).filter(p => p !== phone) }));
  };

  const handleSubmit = async () => {
    // Basic validation before confirming
    if (form.sendMode === 'schedule') {
      if (!form.scheduledAt) {
        setError('Please select a date and time for scheduling.');
        return;
      }
      const when = new Date(form.scheduledAt);
      if (isNaN(when.getTime())) {
        setError('Invalid schedule date/time.');
        return;
      }
      if (when.getTime() <= Date.now()) {
        setError('Scheduled time must be in the future.');
        return;
      }
    }
    setOpenConfirm(true);
  };

  const confirmSend = async () => {
    setOpenConfirm(false);
    setLoading(true);
    setError(null);
    try {
      const payload = {
        audienceType: form.audienceType,
        message: form.message,
        individualPhones: form.audienceType === 'individual' ? form.individualPhones : [],
        sendNow: form.sendMode === 'now',
        scheduledAt: form.sendMode === 'schedule' ? new Date(form.scheduledAt).toISOString() : undefined,
      };
      const resp = editingId
        ? await ApiService.updateSms(editingId, payload)
        : await ApiService.createSms(payload);
      if (resp?.success) {
        setForm({ audienceType: 'all_employers', message: '', individualPhones: [], tempPhone: '', sendMode: 'now', scheduledAt: null });
        setEditingId(null);
        await fetchList();
      }
    } catch (err) {
      console.error('Failed to send SMS:', err);
      setError(err?.message || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>SMS Communications</Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip label="All" color={!category ? 'primary' : 'default'} onClick={() => navigate('/admin/sms')} />
        <Chip label="Sent" color={String(category||'').toLowerCase()==='sent' ? 'primary' : 'default'} onClick={() => navigate('/admin/sms/sent')} />
        <Chip label="Drafts" color={['draft','drafts'].includes(String(category||'').toLowerCase()) ? 'primary' : 'default'} onClick={() => navigate('/admin/sms/drafts')} />
        <Chip label="Failed" color={['fail','failed'].includes(String(category||'').toLowerCase()) ? 'primary' : 'default'} onClick={() => navigate('/admin/sms/failed')} />
        <Chip label="Scheduled" color={['auto','scheduled','auto-send','autosend'].includes(String(category||'').toLowerCase()) ? 'primary' : 'default'} onClick={() => navigate('/admin/sms/scheduled')} />
      </Stack>

      <Grid container spacing={2}>
        {!isListOnly && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title={editingId ? 'Edit SMS' : 'New SMS'} subheader="Send SMS to user groups or individuals" />
              <CardContent>
                <TextField
                  select
                  fullWidth
                  label="Audience"
                  value={form.audienceType}
                  onChange={handleChange('audienceType')}
                  sx={{ mb: 2 }}
                >
                  {audienceOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                </TextField>

                <TextField
                  fullWidth
                  label="Message"
                  value={form.message}
                  onChange={handleChange('message')}
                  multiline
                  minRows={4}
                  sx={{ mb: 2 }}
                />

                {form.audienceType === 'individual' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Individual Phones</Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
                      {(form.individualPhones || []).map((phone) => (
                        <Chip key={phone} label={phone} onDelete={() => handleRemovePhoneChip(phone)} />
                      ))}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="Add phone"
                        value={form.tempPhone || ''}
                        onChange={handleChange('tempPhone')}
                        placeholder="e.g. +15551234567"
                        fullWidth
                      />
                      <Button variant="outlined" onClick={handleAddPhoneChip}>Add</Button>
                    </Stack>
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Delivery</Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={form.sendMode}
                    onChange={(_, val) => val && setForm((prev) => ({ ...prev, sendMode: val }))}
                    size="small"
                  >
                    <ToggleButton value="now">Send Now</ToggleButton>
                    <ToggleButton value="schedule">Schedule</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {form.sendMode === 'schedule' && (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      label="Schedule date & time"
                      value={form.scheduledAt}
                      onChange={(newVal) => setForm((prev) => ({ ...prev, scheduledAt: newVal }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                )}

                <Stack direction="row" spacing={2}>
                  <Button variant="contained" onClick={handleSubmit} disabled={loading || !form.message?.trim()}>
                    {form.sendMode === 'now' ? 'Send Now' : 'Schedule Send'}
                  </Button>
                  {editingId && <Button variant="outlined" onClick={() => setEditingId(null)}>Cancel Edit</Button>}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={isListOnly ? 12 : 6}>
          <Card>
            <CardHeader title="SMS History" subheader="Recent messages and status" />
            <CardContent>
              <div style={{ height: 520, width: '100%' }}>
                <DataGrid
                  rows={list}
                  columns={columns}
                  pageSizeOptions={[5, 10]}
                  loading={loading}
                  disableRowSelectionOnClick
                />
              </div>
              {error && <Typography color="error" sx={{ mt: 1 }}>{String(error)}</Typography>}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>{form.sendMode === 'now' ? 'Confirm Send' : 'Confirm Schedule'}</DialogTitle>
        <DialogContent>
          {form.sendMode === 'now' ? (
            <Typography>Send this SMS to the selected audience now?</Typography>
          ) : (
            <Typography>
              Schedule this SMS for {form.scheduledAt ? new Date(form.scheduledAt).toLocaleString() : 'the selected date/time'}?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
          <Button onClick={confirmSend} variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}